import React from 'react';
import { VALID_GAME_ID } from '@root/constants';
import { useDesignViewport } from '@root/hooks';
import { exitToLobby } from '@root/lib';
import { useGetGameInactivityConfigQuery } from '@root/store/api/game';
import { useActions } from '@root/store/store';
import { useImage } from '@shared/components/ImageContext';
import { useAnalytics } from '@shared/hooks';
import { AnalyticsEvent } from '@shared/services/analytics';
import styles from './Header.module.scss';

type Box = { x: number; y: number; width: number; height: number };

// Design coords. The logo sits over the right column, not the middle.
const LAYOUT = {
  landscape: {
    menu: { x: 40, y: 32, width: 44, height: 44 },
    home: { x: 1356, y: 32, width: 44, height: 44 },
    logo: { x: 827, y: 34, width: 300, height: 167 },
  },
  portrait: {
    menu: { x: 16, y: 18, width: 44, height: 44 },
    home: { x: 330, y: 18, width: 44, height: 44 },
    logo: { x: 125, y: 1, width: 140, height: 78 },
  },
} as const;

const box = (b: Box): React.CSSProperties => ({
  left: b.x,
  top: b.y,
  width: b.width,
  height: b.height,
});

// Menu, logo, home. Balance lives with the roll controls now.
const Header: React.FC = () => {
  const { setMenuPopupOpen } = useActions();
  const { data: viewConfig } = useGetGameInactivityConfigQuery(VALID_GAME_ID);
  const lobbyEnabled = viewConfig?.data?.lobbyEnabled ?? true;
  const { trackPage } = useAnalytics();
  const viewport = useDesignViewport();
  const menuImage = useImage('Icon_Menu');
  const logoImage = useImage('Logo');
  const homeImage = useImage('Icon_Home');

  const L = viewport.portrait ? LAYOUT.portrait : LAYOUT.landscape;

  const handleMenuClick = () => {
    trackPage(AnalyticsEvent.INSTANT_HAMBURGER_OPENED);
    setMenuPopupOpen(true);
  };

  const handleExitClick = () => {
    trackPage(AnalyticsEvent.HOME_BUTTON_CLICKED);
    exitToLobby();
  };

  return (
    <div
      className={styles.layer}
      style={{
        width: viewport.width,
        height: viewport.height,
        transform: `translate(${viewport.x}px, ${viewport.y}px) scale(${viewport.scale})`,
      }}
    >
      <button
        type="button"
        aria-label="menu"
        className={styles.button}
        style={box(L.menu)}
        onClick={handleMenuClick}
      >
        <img src={menuImage.src} onError={menuImage.onError} alt="" />
      </button>

      <div className={styles.logo} style={box(L.logo)}>
        <img src={logoImage.src} onError={logoImage.onError} alt="logo" />
      </div>

      {lobbyEnabled && (
        <button
          type="button"
          aria-label="home"
          className={styles.button}
          style={box(L.home)}
          onClick={handleExitClick}
        >
          <img src={homeImage.src} onError={homeImage.onError} alt="" />
        </button>
      )}
    </div>
  );
};

export default Header;
